import RiDeleteBinLine from '@ben/icons/delete-bin-line';
import RiDeleteColumn from '@ben/icons/delete-column';
import RiDeleteRow from '@ben/icons/delete-row';
import RiInsertColumnLeft from '@ben/icons/insert-column-left';
import RiInsertColumnRight from '@ben/icons/insert-column-right';
import RiInsertRowBottom from '@ben/icons/insert-row-bottom';
import RiInsertRowTop from '@ben/icons/insert-row-top';
import RiTable2 from '@ben/icons/table-2';
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import RIcon from '../remix-icon/remix-icon';
import IframeEmbedControl from './iframe-embed-control';
import ImageUploadControl from './image-upload-control';

export enum HeadingLevel {
  H1 = 'H1',
  H2 = 'H2',
  H3 = 'H3',
  H4 = 'H4',
  H5 = 'H5',
  H6 = 'H6',
}

const DEFAULT_HEADINGS: HeadingLevel[] = [
  HeadingLevel.H1,
  HeadingLevel.H2,
  HeadingLevel.H3,
  HeadingLevel.H4,
];

export interface ToolbarControlsProps {
  toolbar: 'default' | 'basic' | 'marks';
  linkEnabled: boolean;
  tableEnabled: boolean;
  imageUploadEnabled?: boolean;
  iframeEnabled?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
  headings?: HeadingLevel[];
}

const ToolbarControls = ({
  toolbar,
  linkEnabled,
  tableEnabled,
  imageUploadEnabled,
  iframeEnabled,
  onImageUpload,
  headings = DEFAULT_HEADINGS,
}: ToolbarControlsProps) => {
  if (toolbar === 'basic') {
    return (
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
        {linkEnabled && <RichTextEditor.Link />}
      </RichTextEditor.ControlsGroup>
    );
  }

  if (toolbar === 'marks') {
    return (
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
        <RichTextEditor.Underline />
        <RichTextEditor.Strikethrough />
        {linkEnabled && <RichTextEditor.Link />}
      </RichTextEditor.ControlsGroup>
    );
  }

  return (
    <>
      <RichTextEditor.ControlsGroup>
        {headings.map((heading) => {
          const HeadingComponent = RichTextEditor[heading];
          return <HeadingComponent key={heading} />;
        })}
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
        <RichTextEditor.Underline />
        <RichTextEditor.Strikethrough />
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        <RichTextEditor.OrderedList />
        <RichTextEditor.BulletList />
      </RichTextEditor.ControlsGroup>

      <RichTextEditor.ControlsGroup>
        {linkEnabled && <RichTextEditor.Link />}
        {imageUploadEnabled && onImageUpload && (
          <ImageUploadControl onImageUpload={onImageUpload} />
        )}
        {iframeEnabled && <IframeEmbedControl />}
        <RichTextEditor.ClearFormatting />
      </RichTextEditor.ControlsGroup>

      {tableEnabled && (
        <RichTextEditor.ControlsGroup>
          <TableControls />
        </RichTextEditor.ControlsGroup>
      )}
    </>
  );
};

export default ToolbarControls;

const TableControls = () => {
  const { editor } = useRichTextEditorContext();

  return (
    <>
      <RichTextEditor.Control
        onClick={() =>
          editor?.commands.insertTable({
            rows: 3,
            cols: 3,
            withHeaderRow: true,
          })
        }
        aria-label="Insert table (3x3 with header)"
        title="Insert table (3x3 with header)"
      >
        <RIcon icon={RiTable2} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.addColumnBefore()}
        aria-label="Insert column before"
        title="Insert column before"
      >
        <RIcon icon={RiInsertColumnLeft} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.addColumnAfter()}
        aria-label="Insert column after"
        title="Insert column after"
      >
        <RIcon icon={RiInsertColumnRight} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.deleteColumn()}
        aria-label="Delete column"
        title="Delete current column"
      >
        <RIcon icon={RiDeleteColumn} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.addRowBefore()}
        aria-label="Insert row above"
        title="Insert row above"
      >
        <RIcon icon={RiInsertRowTop} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.addRowAfter()}
        aria-label="Insert row below"
        title="Insert row below"
      >
        <RIcon icon={RiInsertRowBottom} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.deleteRow()}
        aria-label="Delete row"
        title="Delete current row"
      >
        <RIcon icon={RiDeleteRow} size="sm" />
      </RichTextEditor.Control>

      <RichTextEditor.Control
        onClick={() => editor?.commands.deleteTable()}
        aria-label="Delete entire table"
        title="Delete entire table"
      >
        <RIcon icon={RiDeleteBinLine} size="sm" />
      </RichTextEditor.Control>
    </>
  );
};
